import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchEvents, fetchPageBySlug } from '../lib/cms';

export default function Arrangementer() {
  const [pageData, setPageData] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      const [eventsData, page] = await Promise.all([
        fetchEvents(),
        fetchPageBySlug('arrangementer').catch(() => null)
      ]);
      setEvents(eventsData || []);
      if (page) setPageData(page);
      setLoading(false);
    }
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <i className="fa-solid fa-spinner animate-spin text-4xl text-dahlia-500 mb-4" />
          <p className="text-forest-900 font-medium">Henter indhold...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-20">
      <Helmet>
        <title>{pageData?.meta_titel?.da || pageData?.meta_title?.da || 'Arrangementer | Dahliahjørnet - H.J.K. Multiservice'}</title>
        <meta name="description" content={pageData?.meta_beskrivelse?.da || pageData?.meta_description?.da || 'Her kan du se og læse mere om alle vores kommende spændende arrangementer på Dahliahjørnet.'} />
      </Helmet>
      {/* Header Banner */}
      <div className="relative h-[40vh] min-h-[300px] bg-forest-950 flex items-center justify-center text-center px-6">
        <img
          src={pageData?.banner_billede?.[0] || 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/c347dbd5-ff95-4119-bc58-7c327c410cd6/arrangementer.jpg'}
          alt={pageData?.titel?.da || 'Arrangementer på Dahliahjørnet'}
          className="absolute inset-0 w-full h-full object-cover object-center opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-forest-950 via-forest-950/50 to-transparent" />
        <div className="relative z-10 max-w-4xl">
          <span className="text-white uppercase tracking-[0.25em] font-bold text-xs sm:text-sm mb-3 block">
            {pageData?.underoverskrift?.da || 'Oplevelser på gården'}
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-white leading-tight">
            {pageData?.titel?.da || 'Arrangementer'}
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 mt-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Events List */}
          <div className="lg:col-span-8 flex flex-col gap-8">
            <div className="flex flex-col gap-2 mb-4">
              <h2 className="text-2xl font-serif font-bold text-forest-950">Vores kommende arrangementer</h2>
              <p className="text-charcoal-600">Her kan du se og læse mere om alle vores kommende spændende arrangementer på gården i Vandel.</p>
            </div>

            {events.length === 0 ? (
              <div className="bg-white p-12 rounded-3xl text-center border border-cream-200">
                <i className="fa-solid fa-calendar-xmark text-4xl text-cream-400 mb-3 block" />
                <p className="text-charcoal-600 font-medium">Der er i øjeblikket ingen planlagte arrangementer.</p>
                <p className="text-sm text-charcoal-500 mt-1">Hold øje med siden eller følg os på Facebook for opdateringer.</p>
              </div>
            ) : (
              <div className="flex flex-col gap-8">
                {events.map((event) => (
                  <div
                    key={event.id}
                    className="bg-white rounded-3xl overflow-hidden shadow-sm border border-cream-200 flex flex-col sm:flex-row"
                  >
                    {/* Event Date Badge / Side */}
                    <div className="bg-black text-white p-8 sm:w-48 flex flex-col items-center justify-center text-center gap-2 border-b sm:border-b-0 sm:border-r border-cream-200">
                      <i className="fa-solid fa-calendar-days text-3xl text-white" />
                      <span className="text-xs font-bold uppercase tracking-wider text-neutral-400">Dato / Tid</span>
                      <span className="text-sm font-semibold text-white leading-tight">
                        {event.dato_tekst?.da || event.date_text?.da || 'Se beskrivelse'}
                      </span>
                    </div>

                    {/* Event Content */}
                    <div className="p-8 flex flex-col gap-4 flex-1">
                      <h3 className="text-xl sm:text-2xl font-serif font-bold text-forest-950 leading-tight">
                        {event.titel?.da || event.title?.da}
                      </h3>
                      <div
                        className="text-charcoal-700 text-sm leading-relaxed prose prose-sm prose-forest max-w-none"
                        dangerouslySetInnerHTML={{ __html: event.beskrivelse?.da || event.content?.da || '' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar / Info */}
          <div className="lg:col-span-4 flex flex-col gap-8">
            {/* Highlights Box */}
            <div className="bg-white text-charcoal-800 p-8 rounded-3xl shadow-md border border-cream-200">
              <h3 className="text-xl font-serif font-bold text-forest-950 mb-6">Hvad kan du finde hos os?</h3>
              <ul className="flex flex-col gap-4 text-sm text-charcoal-700">
                <li className="flex items-start gap-3">
                  <span className="text-black mt-1.5 flex-shrink-0 text-[8px]">
                    <i className="fa-solid fa-circle" />
                  </span>
                  <span>Stort udvalg af pluk selv dahliablomster</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-black mt-1.5 flex-shrink-0 text-[8px]">
                    <i className="fa-solid fa-circle" />
                  </span>
                  <span>Et utal af farveringe blomster i forskellige størrelse</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-black mt-1.5 flex-shrink-0 text-[8px]">
                    <i className="fa-solid fa-circle" />
                  </span>
                  <span>Køb knolde og stiklinger til din egen have</span>
                </li>
              </ul>
            </div>

            {/* Image Box */}
            <div className="rounded-3xl overflow-hidden shadow-md border border-cream-200 aspect-square">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=600,height=600,fit=cover,quality=85,format=auto/assets/c347dbd5-ff95-4119-bc58-7c327c410cd6/arrangementer.jpg"
                alt="Gården i Vandel"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
